// ─────────────────────────────────────────────────────────────────
// Glass-i — Aging Report helpers (Stage 6E.3).
//
// Pure-JS helpers consumed by:
//   - DebtorsAgingPage
//   - CreditorsAgingPage
//   - AgingDetailDrawer / modal
//
// No React, no axios, no JSX so node:test can import these directly.
// Mirrors backend bucket keys + labels — keep in lockstep with
// `backend/.../agingReports/aging.constants.js`.
// ─────────────────────────────────────────────────────────────────

export const AGING_BUCKETS_DETAILED = [
  { key: 'notDue',         label: 'Not Due'    },
  { key: 'bucket0_30',     label: '0–30 days'  },
  { key: 'bucket31_60',    label: '31–60 days' },
  { key: 'bucket61_90',    label: '61–90 days' },
  { key: 'bucket91_180',   label: '91–180 days' },
  { key: 'bucket181_365',  label: '181–365 days' },
  { key: 'bucket365Plus',  label: '365+ days'  }
];

export const AGING_BUCKETS_SIMPLE = [
  { key: 'notDue',       label: 'Not Due' },
  { key: 'bucket0_30',   label: '0–30'    },
  { key: 'bucket31_60',  label: '31–60'   },
  { key: 'bucket61_90',  label: '61–90'   },
  { key: 'bucket90Plus', label: '90+'     }
];

// Backend always emits the detailed bucket fields. The "simple" view
// is a frontend convenience — `bucket90Plus` is a derived sum of the
// 4 buckets beyond 61–90.
export function deriveSimpleBuckets(detailedRow) {
  if (!detailedRow) return {};
  const ninetyPlus =
    toNumber(detailedRow.bucket91_180) +
    toNumber(detailedRow.bucket181_365) +
    toNumber(detailedRow.bucket365Plus);
  return {
    notDue:        toNumber(detailedRow.notDue),
    bucket0_30:    toNumber(detailedRow.bucket0_30),
    bucket31_60:   toNumber(detailedRow.bucket31_60),
    bucket61_90:   toNumber(detailedRow.bucket61_90),
    bucket90Plus:  Math.round(ninetyPlus * 100) / 100
  };
}

// Pick the visible buckets given the mode the operator chose.
export function bucketsForMode(mode) {
  return mode === 'simple' ? AGING_BUCKETS_SIMPLE : AGING_BUCKETS_DETAILED;
}

// Bucket → severity colour. Used by the cell renderer.
export function agingSeverityForBucket(bucketKey) {
  switch (bucketKey) {
    case 'notDue':       return 'default';
    case 'bucket0_30':   return 'green';
    case 'bucket31_60':  return 'gold';
    case 'bucket61_90':  return 'orange';
    case 'bucket91_180': return 'volcano';
    case 'bucket181_365':return 'red';
    case 'bucket365Plus':return 'magenta';
    case 'bucket90Plus': return 'volcano';
    default:             return 'default';
  }
}

function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Pretty-print an INR amount. Returns a string with thousand separators
// and 2dp. Zero renders as the em-dash placeholder by default; pass
// `{ alwaysShow: true }` to render '0.00' instead.
export function formatAgingAmount(n, { alwaysShow = false } = {}) {
  const v = toNumber(n);
  if (!alwaysShow && Math.abs(v) < 0.005) return '—';
  return v.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

// Identical to backend's bucketLabel lookup. Used by drawer + CSV.
export function bucketLabel(bucketKey) {
  const found = AGING_BUCKETS_DETAILED.find((b) => b.key === bucketKey);
  return found ? found.label : (bucketKey || '');
}

// Pick a single display name for the party column. Prefers the linked
// party_name (customer/supplier master); falls back to the ledger
// label when no master linkage exists.
export function pickPartyDisplayName(row) {
  if (!row) return '';
  return row.partyName || row.ledgerName || row.partyCode || row.ledgerCode || '';
}

// Pick a one-line subtitle for the party column. Shows ledger code +
// party code (when distinct) so operators don't have to guess.
export function pickPartySubtitle(row) {
  if (!row) return '';
  const parts = [];
  if (row.partyCode && row.partyCode !== row.ledgerCode) parts.push(row.partyCode);
  if (row.ledgerCode) parts.push(`Ledger ${row.ledgerCode}`);
  return parts.join(' · ');
}

// Re-compute the report summary from the visible rows. Useful when the
// UI applies an in-memory filter (e.g. search) and wants to keep the
// summary cards consistent with what the user sees.
export function computeAgingSummary(rows) {
  const acc = {
    totalOutstanding: 0,
    notDue: 0, bucket0_30: 0, bucket31_60: 0, bucket61_90: 0,
    bucket91_180: 0, bucket181_365: 0, bucket365Plus: 0,
    partyCount: 0, overduePartyCount: 0
  };
  if (!Array.isArray(rows)) return acc;
  for (const row of rows) {
    acc.totalOutstanding += toNumber(row.totalOutstanding);
    for (const b of AGING_BUCKETS_DETAILED) {
      acc[b.key] = roundPaisa(acc[b.key] + toNumber(row[b.key]));
    }
    acc.partyCount += 1;
    if (
      toNumber(row.bucket31_60)  > 0 ||
      toNumber(row.bucket61_90)  > 0 ||
      toNumber(row.bucket91_180) > 0 ||
      toNumber(row.bucket181_365) > 0 ||
      toNumber(row.bucket365Plus) > 0
    ) acc.overduePartyCount += 1;
  }
  acc.totalOutstanding = roundPaisa(acc.totalOutstanding);
  return acc;
}

function roundPaisa(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

// Defensive normaliser — turn whatever the backend handed us into the
// shape the table expects. Useful when an old API version is talking
// to a newer page (forward-compat) or vice versa.
export function normalizeAgingRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    partyId:        r.partyId        || null,
    partyName:      r.partyName      || null,
    partyCode:      r.partyCode      || null,
    partyType:      r.partyType      || null,
    ledgerAccountId: r.ledgerAccountId || null,
    ledgerCode:     r.ledgerCode     || null,
    ledgerName:     r.ledgerName     || null,
    accountType:    r.accountType    || null,
    accountGroup:   r.accountGroup   || null,
    accountSubtype: r.accountSubtype || null,
    totalOutstanding: toNumber(r.totalOutstanding),
    notDue:          toNumber(r.notDue),
    bucket0_30:      toNumber(r.bucket0_30),
    bucket31_60:     toNumber(r.bucket31_60),
    bucket61_90:     toNumber(r.bucket61_90),
    bucket91_180:    toNumber(r.bucket91_180),
    bucket181_365:   toNumber(r.bucket181_365),
    bucket365Plus:   toNumber(r.bucket365Plus),
    oldestOpenDate:  r.oldestOpenDate  || null,
    lastPaymentDate: r.lastPaymentDate || null,
    lastVoucherDate: r.lastVoucherDate || null,
    transactionCount: toNumber(r.transactionCount),
    openLineCount:    toNumber(r.openLineCount)
  }));
}

// Clean the summary query before sending. Strips null / empty so axios
// doesn't render them as the literal string "null".
export function buildAgingQuery(params) {
  const out = {};
  if (!params) return out;
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'boolean') { out[k] = v ? 'true' : 'false'; continue; }
    out[k] = v;
  }
  return out;
}

// CSV export — produces an array of plain objects so the caller can
// pick the column order. Returns the standard summary columns + 7
// detailed buckets. Operators can choose to export the simple view by
// passing { mode: 'simple' }.
export function exportAgingCsvRows(rows, { mode = 'detailed' } = {}) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const base = {
      'Party / Ledger':   pickPartyDisplayName(row),
      'Ledger Code':      row.ledgerCode || '',
      'Party Code':       row.partyCode  || '',
      'Total Outstanding': row.totalOutstanding,
      'Oldest Open Date': row.oldestOpenDate || '',
      'Last Payment':     row.lastPaymentDate || ''
    };
    if (mode === 'simple') {
      const simple = deriveSimpleBuckets(row);
      return {
        ...base,
        'Not Due': simple.notDue,
        '0–30':    simple.bucket0_30,
        '31–60':   simple.bucket31_60,
        '61–90':   simple.bucket61_90,
        '90+':     simple.bucket90Plus
      };
    }
    return {
      ...base,
      'Not Due':      row.notDue,
      '0–30 days':    row.bucket0_30,
      '31–60 days':   row.bucket31_60,
      '61–90 days':   row.bucket61_90,
      '91–180 days':  row.bucket91_180,
      '181–365 days': row.bucket181_365,
      '365+ days':    row.bucket365Plus
    };
  });
}

// Status-line message shown above the aging detail drawer / page so
// the operator knows the report basis. We do NOT use technical terms
// like "FIFO" or "voucher line" in the user-facing copy.
export function friendlyAgingBasisMessage(meta) {
  if (!meta) return '';
  if (meta.invoiceLevelAvailable) {
    return 'Outstanding is computed at the invoice level using payment allocations.';
  }
  return 'This is ledger / voucher-based aging. Invoice-level aging will be enhanced when Sales / Purchase modules are enabled.';
}
